import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { useSiteSettings } from "@/context/SiteSettingsContext";

const CATS = [
  { slug: "all", name: "All Products" },
  { slug: "gpus", name: "Graphics Cards" }, { slug: "cpus", name: "Processors" },
  { slug: "motherboards", name: "Motherboards" }, { slug: "ram", name: "Memory" },
  { slug: "storage", name: "Storage" }, { slug: "psu", name: "Power Supplies" },
  { slug: "cases", name: "Cases" }, { slug: "cooling", name: "Cooling" },
  { slug: "monitors", name: "Monitors" }, { slug: "peripherals", name: "Peripherals" },
  { slug: "desktops", name: "Prebuilts" }, { slug: "laptops", name: "Laptops" },
  { slug: "workstations", name: "Workstations" }, { slug: "printers", name: "Printers" },
  { slug: "networking", name: "Networking" },
];

export default function Catalog() {
  const [params, setParams] = useSearchParams();
  const category = params.get("category") || "all";
  const search = params.get("search") || "";
  const [sort, setSort] = useState(params.get("sort") || "featured");
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(params.get("brand") || "");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const { isPrelaunch } = useSiteSettings();

  useEffect(() => {
    api.get("/products/categories").then(({ data }) => setBrands(data.brands));
  }, []);

  useEffect(() => {
    setLoading(true);
    const p = { category, sort, limit: 60 };
    if (search) p.search = search;
    if (selectedBrand) p.brand = selectedBrand;
    api.get("/products", { params: p }).then(({ data }) => {
      setItems(data.items); setTotal(data.total);
    }).finally(() => setLoading(false));
  }, [category, sort, selectedBrand, search]);

  const setCategory = (slug) => {
    const p = new URLSearchParams(params);
    if (slug === "all") p.delete("category"); else p.set("category", slug);
    setParams(p);
  };

  return (
    <div className="container-tight py-8" data-testid="catalog-page">
      <div className="mb-6">
        <div className="font-mono text-xs tracking-widest text-[#FF5C00] mb-1">// CATALOG</div>
        <h1 className="font-display font-black text-3xl md:text-4xl">
          {search ? `RESULTS FOR "${search.toUpperCase()}"` : CATS.find((c) => c.slug === category)?.name.toUpperCase() || "ALL PRODUCTS"}
        </h1>
        <div className="font-mono text-xs text-zinc-500 mt-1">{total} PRODUCTS</div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-8">
        <aside className="space-y-6">
          <div>
            <div className="font-mono text-xs tracking-widest text-zinc-400 uppercase mb-3">Category</div>
            <div className="space-y-1">
              {CATS.map((c) => (
                <button key={c.slug} data-testid={`filter-cat-${c.slug}`}
                  onClick={() => setCategory(c.slug)}
                  className={`block text-sm w-full text-left px-2 py-1.5 rounded-sm ${category === c.slug ? "bg-[#FF5C00]/10 text-[#FF5C00] font-medium" : "text-zinc-400 hover:text-white hover:bg-zinc-900"}`}>
                  {c.name}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="font-mono text-xs tracking-widest text-zinc-400 uppercase mb-3">Brand</div>
            <Select value={selectedBrand || "any"} onValueChange={(v) => setSelectedBrand(v === "any" ? "" : v)}>
              <SelectTrigger data-testid="filter-brand" className="bg-[#121217] border-zinc-800 rounded-sm">
                <SelectValue placeholder="Any brand" />
              </SelectTrigger>
              <SelectContent className="bg-[#0a0a0c] border-zinc-800 max-h-72">
                <SelectItem value="any">Any brand</SelectItem>
                {brands.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </aside>

        <div>
          <div className="flex items-center justify-between mb-4 pb-4 border-b border-zinc-900">
            <div className="font-mono text-xs text-zinc-500">
              {items.length} shown{isPrelaunch ? " · PREVIEW CATALOG" : ""}
            </div>
            <div className="flex items-center gap-2">
              <span className="font-mono text-xs text-zinc-500">SORT:</span>
              <Select value={sort} onValueChange={setSort}>
                <SelectTrigger data-testid="sort-select" className="w-44 bg-[#121217] border-zinc-800 rounded-sm h-9">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0a0a0c] border-zinc-800">
                  <SelectItem value="featured">Featured</SelectItem>
                  <SelectItem value="price_asc">Price: Low → High</SelectItem>
                  <SelectItem value="price_desc">Price: High → Low</SelectItem>
                  <SelectItem value="newest">Newest</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
              {[...Array(8)].map((_, i) => <Skeleton key={i} className="aspect-[3/4] bg-zinc-900" />)}
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-24 text-zinc-500 font-mono">NO PRODUCTS FOUND</div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="products-grid">
              {items.map((p) => <ProductCard key={p.id} product={p} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
